import { useEffect } from 'react';

/*
  Per-route SEO for a client-rendered SPA.

  The static public/index.html ships a set of default head tags (title,
  description, canonical, Open Graph, Twitter) that describe the homepage.
  Because BrowserRouter swaps components without a page reload, every route
  would otherwise keep those homepage tags — including a canonical that points
  back to "/", which tells search engines the other routes are duplicates of
  the homepage.

  This component updates the EXISTING head tags in place (rather than adding
  new ones) whenever a route mounts, so there is always exactly one <title>,
  one canonical, one description, etc. It intentionally mutates the tags that
  index.html already defines instead of rendering React 19 metadata, which
  would append duplicates alongside the static ones.
*/

const SITE = 'https://lottopickle.com';

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!value) {
    // Nothing to set — leave any existing default in place.
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    // selector looks like `meta[name="x"]` or `meta[property="x"]`
    const match = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setRobots(noindex) {
  let el = document.head.querySelector('meta[name="robots"]');
  if (noindex) {
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, follow');
  } else if (el) {
    // Route is indexable — remove any leftover noindex from a prior route.
    el.setAttribute('content', 'index, follow');
  }
}

/**
 * @param {string}  path        Route path, e.g. "/pricing" (canonical is SITE + path). Defaults to "/".
 * @param {string}  title       Full <title> text.
 * @param {string}  description Meta description.
 * @param {boolean} noindex     When true, marks the page noindex (e.g. account, 404).
 */
export default function Seo({ path = '/', title, description, noindex = false }) {
  useEffect(() => {
    const canonical = SITE + (path === '/' ? '/' : path);

    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    setMeta('meta[property="og:url"]', 'content', canonical);
    setCanonical(canonical);
    setRobots(noindex);
  }, [path, title, description, noindex]);

  return null;
}
